import { OwnersRepository } from '~/repositories/ownersRepository';
import { PayoutMethod } from '~/swagger/portal/Api';
import type { PostOwnerDistributionsDto, PostOwnerDistributionsGroupDto } from '~/swagger/portal/Api';
import type { OwnerDistributionEditingGroup, OwnerDistributionsStoreData } from '~/types/ownerDistributions';

const ownersRepo = new OwnersRepository();

function buildPostOwnerDistributionsPayload(storeData: OwnerDistributionsStoreData): PostOwnerDistributionsDto {
  const filtered = storeData.editingDistributions.filter((d) => {
    if (d.distributionAmount <= 0 || !d.paymentBankAccountId) return false;
    if (d.paymentType === PayoutMethod.Ach && !d.receivingBankAccountId) return false;
    if (d.paymentType === PayoutMethod.Manual && !d.externalTransactionId) return false;
    return true;
  });

  const findCommonData = (group: OwnerDistributionEditingGroup) => {
    const first = filtered.find((d) => d.ownerId === group.ownerId && group.propertyIds.includes(d.propertyId));
    if (!first) return undefined;
    return {
      bankAccountId: first.paymentBankAccountId,
      paymentAccountId: first.receivingBankAccountId,
      paymentType: first.paymentType,
      paymentMethod: first.paymentMethod,
      externalTransactionId: first.externalTransactionId,
      memo: first.commonMemo,
      distributionDate: first.distributionDate,
    };
  };

  const groups: PostOwnerDistributionsGroupDto[] = [];

  for (const group of storeData.editingDistributionGroups) {
    const commonData = findCommonData(group);
    const distributions = group.propertyIds
      .map((propertyId) => {
        const data = filtered.find((d) => d.propertyId === propertyId && d.ownerId === group.ownerId);
        return data ? { propertyId, distributionAmount: data.distributionAmount, memo: data.memo ?? '' } : undefined;
      })
      .filter((x): x is { propertyId: string; distributionAmount: number; memo: string } => Boolean(x));

    if (commonData && distributions.length) {
      const bankAccount = storeData.bankAccounts.find((a) => a.id === commonData.bankAccountId);
      const cashChartOfAccountId = bankAccount?.chartOfAccountIds?.[0] ?? '';
      groups.push({
        ownerId: group.ownerId,
        ...commonData,
        cashChartOfAccountId,
        distributions,
      });
    }
  }

  return { groups };
}

export function useOwnerDistributionsPortal() {
  const submitDistributions = async (storeData: OwnerDistributionsStoreData) => {
    const payload = buildPostOwnerDistributionsPayload(storeData);
    return ownersRepo.submitOwnerDistributions(payload);
  };

  return { submitDistributions };
}
